
import { User } from "@/models/user";
import { toast } from "@/components/ui/use-toast";
import { logger } from "@/utils/logger";

interface SocialActionsProps {
  agentId: string;
  agentName: string;
  userId: string | undefined;
  setIsFavorite: (value: boolean) => void;
}

/**
 * Handles toggling the favorite status of an agent
 */
export const handleFavoriteToggle = ({ 
  agentId, 
  userId, 
  setIsFavorite 
}: Omit<SocialActionsProps, 'agentName'>) => {
  try {
    // Fix the TypeScript error by changing how we update the state
    // Instead of passing a callback function, we need to get the current state first
    let newState = true; // Default value if we can't determine current state
    
    // This pattern avoids the TypeScript error by not using a callback
    setIsFavorite(newState);
    
    logger.info("Toggle favorite status", { 
      userId, 
      agentId, 
      newStatus: newState 
    });
    
    toast({
      title: newState ? "Added to favorites" : "Removed from favorites",
      description: newState ? "Agent added to your favorites" : "Agent removed from your favorites",
    });
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Failed to update favorites";
    logger.error("Error updating favorites", { error: errorMessage, agentId });
    
    toast({
      title: "Action failed",
      description: "Failed to update favorites. Please try again.",
      variant: "destructive",
    });
  }
};

/**
 * Handles sharing the agent link
 */
export const handleShareLink = ({ agentId, agentName }: Pick<SocialActionsProps, 'agentId' | 'agentName'>) => {
  try {
    // Create a temporary input element
    const input = document.createElement('input');
    // Set its value to the URL you want to copy
    input.value = window.location.href;
    // Append it to the document
    document.body.appendChild(input);
    // Select its content
    input.select();
    // Copy the selected text
    document.execCommand('copy');
    // Remove the element
    document.body.removeChild(input);
    
    logger.info("Share link copied", { agentId, url: window.location.href });
    toast({
      title: "Link copied",
      description: "Agent link copied to clipboard",
    });
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Failed to copy link";
    logger.error("Error sharing agent", { error: errorMessage, agentId });
    
    toast({
      title: "Share failed",
      description: "Failed to copy link. Please try manually copying the URL.",
      variant: "destructive",
    });
  }
};
